import { GameState, TradeOffer, SpaceType, PropertyColor, PropertySpace } from '../shared/types.js';
import { BOARD_SPACES } from '../shared/boardSpaces.js';
import { $, show, hide } from './utils.js';
import { PROPERTY_COLOR_HEX } from '../shared/constants.js';
import { sendAction } from './state.js';

let currentState: GameState | null = null;
let myId = '';

export function initTradeModal(socket: any): void {
  // Cancel building a trade
  $('#trade-cancel').addEventListener('click', () => {
    hide($('#trade-modal'));
  });

  // Submit trade offer
  $('#trade-submit').addEventListener('click', () => {
    if (!currentState) return;

    const targetSelect = $('#trade-target-player') as HTMLSelectElement;
    const toPlayerId = targetSelect.value;
    if (!toPlayerId) return;

    const offerProps: number[] = [];
    document.querySelectorAll('#trade-offer-props input[type="checkbox"]:checked').forEach(cb => {
      offerProps.push(parseInt((cb as HTMLInputElement).value, 10));
    });

    const requestProps: number[] = [];
    document.querySelectorAll('#trade-request-props input[type="checkbox"]:checked').forEach(cb => {
      requestProps.push(parseInt((cb as HTMLInputElement).value, 10));
    });

    const offerMoney = parseInt(($('#trade-offer-money') as HTMLInputElement).value, 10) || 0;
    const requestMoney = parseInt(($('#trade-request-money') as HTMLInputElement).value, 10) || 0;

    if (offerProps.length === 0 && requestProps.length === 0 && offerMoney === 0 && requestMoney === 0) {
      alert('Trade must include at least one item.');
      return;
    }

    const offer: TradeOffer = {
      fromPlayerId: myId,
      toPlayerId,
      offerProperties: offerProps,
      requestProperties: requestProps,
      offerMoney: Math.max(0, offerMoney),
      requestMoney: Math.max(0, requestMoney),
    };

    sendAction({ action: 'OFFER_TRADE', tradeOffer: offer });
    hide($('#trade-modal'));
  });

  // Accept / reject incoming trade
  $('#trade-accept').addEventListener('click', () => {
    sendAction({ action: 'ACCEPT_TRADE' });
    hide($('#trade-incoming-modal'));
  });

  $('#trade-reject').addEventListener('click', () => {
    sendAction({ action: 'REJECT_TRADE' });
    hide($('#trade-incoming-modal'));
  });

  // When target player changes, refresh requested properties
  $('#trade-target-player').addEventListener('change', () => {
    populateRequestProps();
    updateTargetSwatch();
  });
}

export function openTradeModal(state: GameState, socketId: string): void {
  currentState = state;
  myId = socketId;

  const me = state.players.find(p => p.id === socketId);
  if (!me) return;

  // Populate target player dropdown
  const targetSelect = $('#trade-target-player') as HTMLSelectElement;
  targetSelect.innerHTML = '';
  for (const p of state.players) {
    if (p.id === socketId || p.isBankrupt) continue;
    const opt = document.createElement('option');
    opt.value = p.id;
    const botTag = p.isBot ? ' 🤖' : '';
    opt.textContent = `${p.avatar || ''} ${p.name}${botTag}`.trim();
    targetSelect.appendChild(opt);
  }

  // Populate my properties (offer side)
  populateOfferProps(state, me);

  // Populate target's properties (request side)
  populateRequestProps();
  updateTargetSwatch();

  // Reset money inputs
  ($('#trade-offer-money') as HTMLInputElement).value = '0';
  ($('#trade-request-money') as HTMLInputElement).value = '0';

  show($('#trade-modal'));
}

function updateTargetSwatch(): void {
  if (!currentState) return;
  const swatch = document.getElementById('trade-target-swatch');
  if (!swatch) return;
  const targetId = ($('#trade-target-player') as HTMLSelectElement).value;
  const target = currentState.players.find(p => p.id === targetId);
  if (!target) {
    swatch.style.display = 'none';
    return;
  }
  swatch.style.display = 'inline-flex';
  swatch.style.background = target.color;
  swatch.textContent = target.avatar || '?';
}

function makePlayerBadge(player: { color: string; avatar: string }): HTMLElement {
  const badge = document.createElement('span');
  badge.className = 'trade-player-badge';
  badge.style.background = player.color;
  badge.textContent = player.avatar || '?';
  return badge;
}

function populateOfferProps(state: GameState, me: { properties: number[]; id: string }): void {
  const container = $('#trade-offer-props');
  container.innerHTML = '';
  for (const pos of me.properties) {
    const owned = state.properties.find(p => p.position === pos);
    if (owned && owned.houses > 0) continue; // can't trade with houses
    container.appendChild(createPropCheckbox(pos));
  }
  if (container.children.length === 0) {
    container.textContent = 'No tradeable properties';
  }
}

function populateRequestProps(): void {
  if (!currentState) return;
  const container = $('#trade-request-props');
  container.innerHTML = '';
  const targetId = ($('#trade-target-player') as HTMLSelectElement).value;
  const target = currentState.players.find(p => p.id === targetId);
  if (!target) return;
  for (const pos of target.properties) {
    const owned = currentState.properties.find(p => p.position === pos);
    if (owned && owned.houses > 0) continue;
    container.appendChild(createPropCheckbox(pos));
  }
  if (container.children.length === 0) {
    container.textContent = 'No tradeable properties';
  }
}

function createPropCheckbox(pos: number): HTMLLabelElement {
  const space = BOARD_SPACES[pos];
  const label = document.createElement('label');
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.value = String(pos);
  label.appendChild(cb);

  // Color dot
  if (space.type === SpaceType.PROPERTY) {
    const dot = document.createElement('span');
    dot.className = 'trade-prop-color';
    dot.style.backgroundColor = PROPERTY_COLOR_HEX[(space as PropertySpace).color] || '#888';
    label.appendChild(dot);
  } else if (space.type === SpaceType.RAILROAD) {
    const dot = document.createElement('span');
    dot.className = 'trade-prop-color';
    dot.style.backgroundColor = '#333';
    label.appendChild(dot);
  } else if (space.type === SpaceType.UTILITY) {
    const dot = document.createElement('span');
    dot.className = 'trade-prop-color';
    dot.style.backgroundColor = '#888';
    label.appendChild(dot);
  }

  label.appendChild(document.createTextNode(space.name));
  return label;
}

export function checkTradeIncoming(state: GameState, socketId: string): void {
  if (state.pendingTrade && state.pendingTrade.toPlayerId === socketId) {
    showIncomingTrade(state);
  } else {
    hide($('#trade-incoming-modal'));
  }
}

function showIncomingTrade(state: GameState): void {
  const offer = state.pendingTrade;
  const from = offer && state.players.find(p => p.id === offer.fromPlayerId);
  if (!offer || !from) {
    hide($('#trade-incoming-modal'));
    return;
  }
  const botTag = from.isBot ? ' 🤖' : '';

  const title = $('#trade-incoming-title');
  title.innerHTML = '';
  title.appendChild(document.createTextNode('Trade Offer from '));
  title.appendChild(makePlayerBadge(from));
  title.appendChild(document.createTextNode(` ${from.name}${botTag}`));

  const fromName = $('#trade-from-name');
  fromName.innerHTML = '';
  fromName.appendChild(makePlayerBadge(from));
  fromName.appendChild(document.createTextNode(` ${from.name}${botTag} Offers`));

  // Offered items
  const offerDiv = $('#trade-incoming-offer');
  offerDiv.innerHTML = '';
  for (const pos of offer.offerProperties) {
    const space = BOARD_SPACES[pos];
    if (!space) continue;
    const item = document.createElement('div');
    item.className = 'trade-item';
    if (space.type === SpaceType.PROPERTY) {
      const dot = document.createElement('span');
      dot.className = 'trade-prop-color';
      dot.style.backgroundColor = PROPERTY_COLOR_HEX[(space as PropertySpace).color] || '#888';
      item.appendChild(dot);
    }
    item.appendChild(document.createTextNode(space.name));
    offerDiv.appendChild(item);
  }
  if (offer.offerMoney > 0) {
    const item = document.createElement('div');
    item.className = 'trade-item';
    item.textContent = `$${offer.offerMoney.toLocaleString()}`;
    offerDiv.appendChild(item);
  }
  if (offer.offerProperties.length === 0 && offer.offerMoney === 0) {
    offerDiv.textContent = 'Nothing';
  }

  // Requested items
  const reqDiv = $('#trade-incoming-request');
  reqDiv.innerHTML = '';
  for (const pos of offer.requestProperties) {
    const space = BOARD_SPACES[pos];
    if (!space) continue;
    const item = document.createElement('div');
    item.className = 'trade-item';
    if (space.type === SpaceType.PROPERTY) {
      const dot = document.createElement('span');
      dot.className = 'trade-prop-color';
      dot.style.backgroundColor = PROPERTY_COLOR_HEX[(space as PropertySpace).color] || '#888';
      item.appendChild(dot);
    }
    item.appendChild(document.createTextNode(space.name));
    reqDiv.appendChild(item);
  }
  if (offer.requestMoney > 0) {
    const item = document.createElement('div');
    item.className = 'trade-item';
    item.textContent = `$${offer.requestMoney.toLocaleString()}`;
    reqDiv.appendChild(item);
  }
  if (offer.requestProperties.length === 0 && offer.requestMoney === 0) {
    reqDiv.textContent = 'Nothing';
  }

  show($('#trade-incoming-modal'));
}
